import React, { useCallback, useRef, useState } from 'react'
import moment from 'moment'
import { tz } from 'moment-timezone'
import styled from 'styled-components'

import FullScreenPlot from 'pages/Device/ThermostatRthPanel/Chart/FullScreenPlot'
import Modal from 'components/Modal'
import RthHeaderControl from 'pages/shared/Device/RthHeaderControl'
import ServerlessRthChart from 'pages/Device/ThermostatRthPanel/ServerlessRthChart'
import Spinner from 'components/Spinner'

import { isNoDataAvailable } from 'pages/Device/ThermostatRthPanel/Chart/rthDataParser'

import { longDate } from 'helpers/formatters'
import { ExportGraphAsImage } from 'helpers/plotly/exportGraphAsImage'

import useDevice from 'hooks/useDevice'
import useServerlessRth from 'hooks/useServerlessRth'
import { useSessionContext } from 'hooks/SessionContext'

type Props = {
  deviceId: string
  timeZone: string
}

const DATE_FORMAT = 'YYYY-MM-DD'

const RthChart = ({ deviceId, timeZone }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  const [isFullScreenPlotZoomed, setIsFullScreenPlotZoomed] = useState(false)
  const [isMouseHoverFrozen, setIsMouseHoverFrozen] = useState(false)
  const [isZoomed, setIsZoomed] = useState(false)
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())
  const [selectedDate, setSelectedDate] = useState(moment().tz(timeZone).format(DATE_FORMAT))
  const [showFullScreen, setShowFullScreen] = useState(false)

  const { session: { dealerId } } = useSessionContext()

  const { rthData, isRTHDataLoaded } = useServerlessRth(
    dealerId,
    deviceId,
    selectedDate,
    selectedDate,
    timeZone,
    refreshTimestamp
  )

  const { device, isDeviceLoaded } = useDevice(dealerId, deviceId)

  const onChangeDate = useCallback((date) => {
    setSelectedDate(tz(date, timeZone).format(DATE_FORMAT))
    setIsZoomed(false)
  }, [timeZone])

  const onExportCallback = (selectedDate) => {
    const plotId = showFullScreen ? 'full-plotly' : 'plotly-serverless-rth-chart'
    const plotlyDiv = document.getElementById(plotId)

    ExportGraphAsImage(
      plotlyDiv,
      canvasRef.current,
      'runtime_' + deviceId + '_' + selectedDate,
      longDate(selectedDate)
    )
  }

  const onRefresh = useCallback(() => {
    setRefreshTimestamp(moment().valueOf())
    setIsZoomed(false)
  }, [])

  const startTime = tz(`${selectedDate}`, DATE_FORMAT, timeZone)
    .startOf('day')
    .format(DATE_FORMAT + ' HH:mm:ss')
  const endTime = tz(`${selectedDate}`, DATE_FORMAT, timeZone)
    .endOf('day')
    .format(DATE_FORMAT + ' HH:mm:ss')

  if (!isDeviceLoaded || !isRTHDataLoaded) return <Spinner variant="page" />

  return (
    <>
      <RthHeaderControl
        date={selectedDate}
        displayResetZoom={isZoomed}
        dateChangedCallback={onChangeDate}
        zoomResetCallback={() => setIsZoomed(false)}
        refreshCallback={onRefresh}
        rthVariant="RTH"
        freezeMouseHover={isMouseHoverFrozen}
        isNoData={isNoDataAvailable(rthData)}
        isRTHDataLoaded={isRTHDataLoaded}
        onExportCallback={onExportCallback}
        isFullScreen={showFullScreen}
        fullScreenIconClickedCallback={() => setShowFullScreen(true)}
        displayHomeownerNotification
      />

      <ServerlessRthChart
        rthDate={selectedDate}
        startTime={startTime}
        endTime={endTime}
        timeZone={timeZone}
        deviceId={deviceId}
        showFullScreen={showFullScreen}
        isControlHub={device?.deviceType === 'control_hub'}
        updateSelectedLegendsCallback={() => {}}
        zoomedCallback={(newValue) => setIsZoomed(newValue)}
        freezeMouseHoverCallback={(newValue) => setIsMouseHoverFrozen(newValue)}
        freezeMouseHover={isMouseHoverFrozen}
        isZoomed={isZoomed}
        rthData={rthData || null}
        isRTHDataLoaded={isRTHDataLoaded}
      />

      <S.Canvas ref={canvasRef} />

      <Modal
        closeCallback={() => setShowFullScreen(false)}
        isDisplayed={showFullScreen}
        dataTestID="fullscreen_rth_close"
        fullScreen
      >
        <S.RthHeaderContainer>
          <RthHeaderControl
            date={selectedDate}
            displayResetZoom={isFullScreenPlotZoomed}
            dateChangedCallback={onChangeDate}
            zoomResetCallback={() => setIsFullScreenPlotZoomed(false)}
            refreshCallback={onRefresh}
            rthVariant="RTH"
            freezeMouseHover={isMouseHoverFrozen}
            isNoData={isNoDataAvailable(rthData)}
            isRTHDataLoaded={isRTHDataLoaded}
            onExportCallback={onExportCallback}
            isFullScreen={showFullScreen}
            fullScreenIconClickedCallback={() => setShowFullScreen(true)}
          />
        </S.RthHeaderContainer>

        <FullScreenPlot
          startTime={startTime}
          endTime={endTime}
          chartDivId="full-plotly"
          timeZone={timeZone}
          deviceId={deviceId}
          isZoomed={isFullScreenPlotZoomed}
          zoomedCallback={(isZoomed) => { setIsFullScreenPlotZoomed(isZoomed) }}
          rthData={rthData}
          isRTHDataLoaded={isRTHDataLoaded}
          updateSelectedLegendsCallback={() => {}}
        />
      </Modal>
    </>
  )
}

export default RthChart

const S = {
  Canvas: styled.canvas`
    display: none;
  `,
  RthHeaderContainer: styled.div`
    padding-right: 30px;
  `
}
